import React, { useEffect, useState } from 'react';
import Header from '../../../components/Header';
import FooterTwo from '../../../components/Footer/index';
import ScrollToTop from '../../../components/ScrollTop';
import PageBanner from '../../../components/PageBanner';

import { FaWifi, FaCoffee, FaGamepad, FaUsers, FaMapMarkerAlt, FaFileAlt, FaShieldAlt } from 'react-icons/fa';

import { getHostel } from '../../../services/data.service';
import { mediaUrl } from '../../../services/assetUrl';

import './hostel.scss';

// Icon key strings (stored in the DB) mapped to react-icons.
const PILLAR_ICONS = {
  location: FaMapMarkerAlt,
  amenities: FaWifi,
  policies: FaFileAlt,
};
const FACILITY_ICONS = {
  mess: FaCoffee,
  games: FaGamepad,
  security: FaShieldAlt,
};

const Hostel = () => {
    const [activePoliciesTab, setActivePoliciesTab] = useState('rules');
    const [activeBlockTab, setActiveBlockTab] = useState('boys');

    // DB-only: the whole page comes from the backend singleton.
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await getHostel();
                const doc = res?.data?.data || res?.data || null;
                if (active && doc && doc.blocks) setContent(doc);
            } catch (e) {
                // leave content null -> unavailable state
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, []);

    const { hero = {}, about = {}, facilities = {}, blocks = {}, policies = {}, committee = {} } = content || {};

    const boysData = blocks.boys || [];
    const girlsData = blocks.girls || [];
    const messData = blocks.mess || [];

    // Hero copy to fill the (taller) banner's left column: admin subtitle or a
    // default, plus info pills derived from the block data.
    const hostelDescription = hero.subtitle || "On-campus residential facilities for boys and girls — safe, comfortable, and minutes from academics, with dining, amenities, and round-the-clock support.";
    const hostelBlockCount = boysData.length + girlsData.length;
    const hostelBeds = [...boysData, ...girlsData].reduce((n, b) => n + (Number(b.capacity) || 0), 0);
    const hostelFacts = [
        hostelBlockCount ? `${hostelBlockCount} Hostel Block${hostelBlockCount === 1 ? "" : "s"}` : null,
        hostelBeds ? `${hostelBeds.toLocaleString()} Beds` : null,
    ].filter(Boolean);

    const policyLists = {
        rules: policies.rules || [],
        documents: policies.documents || [],
        antiragging: policies.antiRagging || [],
    };
    const activeList = policyLists[activePoliciesTab] || [];

    // Loading / unavailable states (DB-only, no bundled fallback).
    if (loading || !content) {
        return (
            <div className="hostel-page">
                <Header parentMenu="facilities" menuCategoryEnable="enable" />
                <div className="container" style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "4rem 0" }}>
                    <p style={{ fontSize: "1.05rem", color: "#6b7280" }}>
                        {loading ? "Loading hostel information…" : "Hostel information is currently unavailable. Please check back later."}
                    </p>
                </div>
                <ScrollToTop />
                <FooterTwo />
            </div>
        );
    }

    return (
        <div className="hostel-page">
            <Header parentMenu="facilities" menuCategoryEnable="enable" />

            <PageBanner
                className="page-banner--tall"
                title={hero.title}
                eyebrow={hero.eyebrow}
                subtitle={hostelDescription}
                facts={hostelFacts}
                image={hero.bannerImage ? mediaUrl(hero.bannerImage) : undefined}
            />

            <div className="hostel-main">

                {/* 1. About NCET Hostels */}
                <section className="hostel-about">
                    <div className="container">
                        <div className="hostel-about__grid">
                            <div className="hostel-about__content">
                                <h2>{about.heading}</h2>
                                {(about.paragraphs || []).map((para, idx) => (
                                    <p key={idx}>{para}</p>
                                ))}
                            </div>
                            <div className="hostel-about__pillars">
                                {(about.pillars || []).map((pillar, idx) => {
                                    const Icon = PILLAR_ICONS[pillar.icon] || FaMapMarkerAlt;
                                    return (
                                        <div className="hostel-pillar" key={idx}>
                                            <div className="hostel-pillar__icon"><Icon size="28" /></div>
                                            <div className="hostel-pillar__info">
                                                <h3>{pillar.title}</h3>
                                                <p>{pillar.text}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </section>

                {/* 2. Facilities Section */}
                <section className="hostel-facilities">
                    <div className="container">
                        <div className="hostel-facilities__grid">
                            <div className="hostel-facilities__image-wrapper">
                                <img src={mediaUrl(facilities.image)} alt="Hostel Infrastructure" />
                            </div>
                            <div>
                                <div className="hostel-facilities__header">
                                    <span>{facilities.eyebrow}</span>
                                    <h2>{facilities.heading}</h2>
                                    <p style={{ marginTop: '15px', color: '#6b7280' }}>{facilities.subtitle}</p>
                                </div>
                                <div className="hostel-facilities__list">
                                    {(facilities.items || []).map((item, idx) => {
                                        const Icon = FACILITY_ICONS[item.icon] || FaCoffee;
                                        return (
                                            <div className="hostel-facility-item" key={idx}>
                                                <div className="hostel-facility-item__icon"><Icon /></div>
                                                <div className="hostel-facility-item__content">
                                                    <h3>{item.title}</h3>
                                                    <p>{item.desc}</p>
                                                </div>
                                                <div className="hostel-facility-item__tag">{item.tag}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 3. Hostel Blocks Tabs Section */}
                <section className="hostel-blocks">
                    <div className="container">
                        <div className="hostel-blocks__header">
                            <h2>{blocks.heading}</h2>
                        </div>
                        <div className="hostel-blocks__tabs">
                            <button
                                className={`hostel-blocks__tab ${activeBlockTab === 'boys' ? 'is-active' : ''}`}
                                onClick={() => setActiveBlockTab('boys')}
                            >Boys Hostel</button>
                            <button
                                className={`hostel-blocks__tab ${activeBlockTab === 'girls' ? 'is-active' : ''}`}
                                onClick={() => setActiveBlockTab('girls')}
                            >Girls Hostel</button>
                            <button
                                className={`hostel-blocks__tab ${activeBlockTab === 'mess' ? 'is-active' : ''}`}
                                onClick={() => setActiveBlockTab('mess')}
                            >Mess Timings</button>
                        </div>

                        <div className="hostel-blocks__grid">
                            {activeBlockTab === 'boys' && boysData.map((block, idx) => (
                                <div className="hostel-block-card" key={idx}>
                                    <h3>{block.name} (Block {block.no})</h3>
                                    <p>Total Rooms: {block.rooms}</p>
                                    <div className="capacity">
                                        <FaUsers /> Capacity: {block.capacity}
                                    </div>
                                </div>
                            ))}
                            {activeBlockTab === 'girls' && girlsData.map((block, idx) => (
                                <div className="hostel-block-card" key={idx}>
                                    <h3>{block.name} (Block {block.no})</h3>
                                    <p>Total Rooms: {block.rooms}</p>
                                    <div className="capacity">
                                        <FaUsers /> Capacity: {block.capacity}
                                    </div>
                                </div>
                            ))}
                            {activeBlockTab === 'mess' && messData.map((meal, idx) => (
                                <div className="hostel-block-card" key={idx}>
                                    <h3>{meal.meal}</h3>
                                    <p style={{ marginTop: '10px' }}><strong>Working Days:</strong><br/>{meal.working}</p>
                                    <p style={{ marginTop: '10px' }}><strong>Holidays:</strong><br/>{meal.holidays}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* 4. Policies & Rules Tabs */}
                <section className="hostel-policies">
                    <div className="container">
                        <div className="hostel-policies__header">
                            <h2>{policies.heading}</h2>
                        </div>
                        <div className="hostel-policies__tabs">
                            <button
                                className={`hostel-policies__tab ${activePoliciesTab === 'rules' ? 'is-active' : ''}`}
                                onClick={() => setActivePoliciesTab('rules')}
                            >Rules & Regulations</button>
                            <button
                                className={`hostel-policies__tab ${activePoliciesTab === 'documents' ? 'is-active' : ''}`}
                                onClick={() => setActivePoliciesTab('documents')}
                            >Documents Required</button>
                            <button
                                className={`hostel-policies__tab ${activePoliciesTab === 'antiragging' ? 'is-active' : ''}`}
                                onClick={() => setActivePoliciesTab('antiragging')}
                            >Anti-Ragging Policy</button>
                        </div>

                        <div className="hostel-policies__content">
                            <ul className="hostel-policies__list">
                                {activeList.map((item, idx) => (
                                    <li key={idx}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={activePoliciesTab === 'antiragging' ? '#EF4444' : 'currentColor'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* 5. Hostel Team / Committee */}
                <section className="hostel-team">
                    <div className="container">
                        <div className="hostel-team__header">
                            <h2>{committee.heading}</h2>
                            <p>{committee.subtitle}</p>
                        </div>
                        <div className="hostel-team__grid">
                            {(committee.roles || []).map((role, idx) => (
                                <div className="hostel-team-card" key={idx}>
                                    <div className="hostel-team-card__icon">
                                        <FaUsers size="40" />
                                    </div>
                                    <h3>{role.title}</h3>
                                    <p>{role.role}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

            </div>

            <ScrollToTop />
            <FooterTwo />
        </div>
    );
};

export default Hostel;
